import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { useAuth } from '../app/auth-context'
import { Button, Card, ErrorNote, TextInput } from '../components/ui'
import { ApiError } from '../lib/api'
import { formatMobile, toLatinDigits, toPersianDigits } from '../lib/fa'
import { requestCode, verifyCode } from '../lib/staff-api'

const messageOf = (error: unknown): string =>
  error instanceof ApiError ? error.messageFa : 'مشکلی پیش آمد. دوباره تلاش کنید.'

/**
 * Two steps on one page: mobile, then code. The API answers the first step the same for every
 * number, so an unknown mobile only finds out at the second step — by its code never arriving.
 */
export function LoginPage() {
  const { status, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  const [step, setStep] = useState<'mobile' | 'code'>('mobile')
  const [mobile, setMobile] = useState('')
  const [code, setCode] = useState('')
  const [devCode, setDevCode] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (status === 'authenticated') return <Navigate to={from} replace />

  const submitMobile = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const result = await requestCode(mobile)
      setDevCode(result.devCode)
      setStep('code')
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const submitCode = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      signIn(await verifyCode(mobile, code))
      navigate(from, { replace: true })
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const digitsOnly = (value: string, max: number) =>
    toLatinDigits(value).replace(/\D/g, '').slice(0, max)

  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <Card className="w-full max-w-sm">
        <h1 className="text-xl font-bold">ورود به پیشخوان</h1>
        <p className="mb-6 mt-1 text-sm text-muted">فقط برای کارکنان بیمه گلد</p>

        {step === 'mobile' ? (
          <form onSubmit={submitMobile} className="flex flex-col gap-4">
            <TextInput
              label="شماره موبایل"
              type="tel"
              inputMode="numeric"
              dir="ltr"
              autoFocus
              placeholder="۰۹۱۲۳۴۵۶۷۸۹"
              value={toPersianDigits(mobile)}
              onChange={(e) => setMobile(digitsOnly(e.target.value, 11))}
            />
            {error ? <ErrorNote>{error}</ErrorNote> : null}
            <Button type="submit" loading={busy} disabled={mobile.length !== 11}>
              دریافت کد
            </Button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="flex flex-col gap-4">
            <p className="text-sm text-muted">
              اگر {formatMobile(mobile.slice(1))} در فهرست کارکنان باشد، کد ورود برایش پیامک شد.
            </p>
            {devCode ? (
              <p className="rounded-xl bg-sunken px-3 py-2 text-xs text-muted">
                کد آزمایشی: {toPersianDigits(devCode)}
              </p>
            ) : null}
            <TextInput
              label="کد ورود"
              inputMode="numeric"
              dir="ltr"
              autoFocus
              autoComplete="one-time-code"
              value={toPersianDigits(code)}
              onChange={(e) => setCode(digitsOnly(e.target.value, 4))}
            />
            {error ? <ErrorNote>{error}</ErrorNote> : null}
            <Button type="submit" loading={busy} disabled={code.length !== 4}>
              ورود
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setStep('mobile')
                setCode('')
                setError(null)
              }}
            >
              تغییر شماره
            </Button>
          </form>
        )}
      </Card>
    </div>
  )
}
